import type { UsageStatsResponse } from "@/lib/types";

import { DailyBarChart } from "./DailyBarChart";
import { DailyStackedChart } from "./DailyStackedChart";
import { formatDuration, formatInt, formatTokens, TOKEN_TYPE_COLORS } from "./formatters";
import { PricingComparison } from "./PricingComparison";
import { ToolUsageTable } from "./ToolUsageTable";

function StatCard({
  label,
  value,
  colorClass = "text-cyber-text",
}: {
  label: string;
  value: string;
  colorClass?: string;
}) {
  return (
    <div className="rounded-lg border border-cyber-border bg-cyber-surface/30 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-cyber-muted/70">{label}</div>
      <div className={`mt-1 text-sm ${colorClass}`}>{value}</div>
    </div>
  );
}

export function StatsAdvanced({ data }: { data: UsageStatsResponse }) {
  const { totals, byDay, toolUsage } = data;

  const cacheDenominator = totals.cacheReadTokens + totals.cacheWriteTokens + totals.inputTokens;
  const cacheEfficiency =
    cacheDenominator === 0 ? 0 : Math.round((totals.cacheReadTokens / cacheDenominator) * 100);

  const agenticDepthData = byDay.map((row) => ({
    day: row.day,
    responses: row.responses,
    value: row.avgStepCount,
  }));

  const latencyData = byDay
    .filter((row) => row.avgLatencyMs > 0)
    .map((row) => ({
      day: row.day,
      responses: row.responses,
      value: row.avgLatencyMs,
    }));

  return (
    <>
      {/* Token breakdown cards */}
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-6">
        <StatCard label="Total" value={formatTokens(totals.totalTokens)} />
        <StatCard
          label="Output"
          value={formatTokens(totals.outputTokens)}
          colorClass={TOKEN_TYPE_COLORS.output.label}
        />
        <StatCard
          label="Cache reads"
          value={formatTokens(totals.cacheReadTokens)}
          colorClass={TOKEN_TYPE_COLORS.cacheRead.label}
        />
        <StatCard
          label="Cache writes"
          value={formatTokens(totals.cacheWriteTokens)}
          colorClass={TOKEN_TYPE_COLORS.cacheWrite.label}
        />
        <StatCard
          label="Base input"
          value={formatTokens(totals.inputTokens)}
          colorClass={TOKEN_TYPE_COLORS.input.label}
        />
        <StatCard
          label="Reasoning"
          value={formatTokens(totals.reasoningTokens)}
          colorClass={TOKEN_TYPE_COLORS.reasoning.label}
        />
      </div>

      {/* Summary row: cache efficiency + tool stats + latency */}
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        <StatCard label="Cache efficiency" value={`${cacheEfficiency}%`} />
        <StatCard label="Avg steps / prompt" value={String(totals.avgStepCount)} />
        <StatCard
          label="Avg latency"
          value={totals.avgLatencyMs > 0 ? formatDuration(totals.avgLatencyMs) : "N/A"}
        />
        <StatCard
          label="Tool error rate"
          value={
            totals.totalToolCalls > 0
              ? `${((totals.totalToolErrors / totals.totalToolCalls) * 100).toFixed(1)}%`
              : "N/A"
          }
        />
      </div>

      {/* Daily token breakdown stacked chart */}
      <section className="rounded-lg border border-cyber-border bg-cyber-surface/20 p-3">
        <h3 className="mb-3 text-xs uppercase tracking-wide text-cyber-muted/80">
          Daily token breakdown
        </h3>
        <DailyStackedChart data={byDay} />
      </section>

      {/* Two-column: Agentic Depth + Latency */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {/* Agentic depth chart */}
        <section className="rounded-lg border border-cyber-border bg-cyber-surface/20 p-3">
          <h3 className="mb-3 text-xs uppercase tracking-wide text-cyber-muted/80">
            Agentic depth (avg steps / prompt)
          </h3>
          <DailyBarChart
            data={agenticDepthData}
            color="rgb(var(--cyber-magenta))"
            valueLabel="steps"
            formatValue={(v) => v.toFixed(1)}
          />
        </section>

        {/* Response latency chart */}
        <section className="rounded-lg border border-cyber-border bg-cyber-surface/20 p-3">
          <h3 className="mb-3 text-xs uppercase tracking-wide text-cyber-muted/80">
            Avg response latency
          </h3>
          {latencyData.length > 0 ? (
            <DailyBarChart
              data={latencyData}
              color="rgb(var(--cyber-green))"
              valueLabel=""
              formatValue={formatDuration}
            />
          ) : (
            <p className="py-8 text-center text-xs text-cyber-muted/60">
              No latency data available.
            </p>
          )}
        </section>
      </div>

      {/* Tool usage table */}
      <section className="rounded-lg border border-cyber-border bg-cyber-surface/20 p-3">
        <h3 className="mb-3 text-xs uppercase tracking-wide text-cyber-muted/80">
          Tool usage ({formatInt(totals.totalToolCalls)} total calls)
        </h3>
        <ToolUsageTable data={toolUsage} />
      </section>

      {/* Pricing comparison */}
      <section className="rounded-lg border border-cyber-border bg-cyber-surface/20 p-3">
        <h3 className="mb-3 text-xs uppercase tracking-wide text-cyber-muted/80">
          Estimated API cost (if pay-per-token)
        </h3>
        <PricingComparison byModel={data.byModel} />
      </section>
    </>
  );
}
